<template>
    <div>

        <Card :header="$t('priceAnalysis')" overlay :loading="requestData1.getLoading()">

            <template #header>
                <div class="d-flex gap-2 align-items-center">
                    <span>{{ $t('priceAnalysis') }}</span>
                    <ButtonIcon
                        v-b-tooltip.hover.noninteractive.bottom
                        icon="download"
                        variant="light"
                        :icon-size="14"
                        class="p-1 rounded-max mb-auto width-25 height-25"
                        :title="$t('downloadTheEntireReport')"
                        :to="{ name: 'DataExport' }"
                    />
                </div>
            </template>

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <MultiselectAvailableCampaigns ref="MultiselectAvailableCampaigns" :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                        <Multiselect
                            v-if="showProductGroupsResults"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            :selected-options-label="$t('selectedGroups')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="groups = $event"
                            @loading="spaceGroupsLoading = $event"
                        />
                        <Multiselect
                            :fetch-options-function="fetchProducerProducts"
                            :placeholder="$t('findProduct')"
                            :selected-options-label="$t('selectedProducts')"
                            preserve-search
                            asynchronous
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading && !spaceProductsLoading"
                            show-checkboxes
                            show-selected
                            multiple
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="products = $event"
                            @loading="spaceProductsLoading = $event"
                        />
                        <FormCategoryTreeSelect
                            v-if="!showProductGroupsResults"
                            ref="FormCategoryTreeSelect"
                            :value="categories"
                            :disabled="loading"
                            class="width-200"
                            :clearable="false"
                            @change="categories = $event"
                            @loading="categoriesLoading = $event"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

            <ErrorMessage :value="requestData1.getError()" />
            <ErrorMessage :value="requestData2.getError()" />

            <div class="dropdown-divider border-bottom border-light"></div>

            <div class="d-flex gap-4 flex-column">

                <ProductList />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AveragePrice />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AveragePriceInTime />

                <div class="dropdown-divider border-bottom border-light"></div>

                <AverageQuantityInTime />

            </div>

        </Card>

    </div>
</template>



<script>
    import Multiselect from '@/components/common/Multiselect.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import ProductList from './ProductList.vue';
    import AveragePrice from './AveragePrice.vue';
    import AveragePriceInTime from './AveragePriceInTime.vue';
    import AverageQuantityInTime from './AverageQuantityInTime.vue';

    export default {
        name: 'PriceDistribution',
        components: {
            Multiselect,
            MultiselectAvailableCampaigns,
            ProductList,
            AveragePrice,
            AveragePriceInTime,
            AverageQuantityInTime,
            FormCategoryTreeSelect,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestData1: this.$req('space/ProductsPriceAggregatedReport'),
                requestData2: this.$req('space/ProducerTransactionsItemsQuantityPrice'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestSpaceGroups: this.$req('producerProductGroups/spaceGroups'),
                producerProduct: this.$req('space/ProducerProducts'),
                showProductGroupsResults: false,
                campaigns: [],
                groups: [],
                products: [],
                categories: [],
                campaignsLoading: false,
                spaceGroupsLoading: false,
                spaceProductsLoading: false,
                categoriesLoading: false,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = { startDate, endDate };
                if (this.products?.length) requestParams.products = this.products.join(',');
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = this.categories.join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = this.groups.join(',');
                return { urlParams, requestParams };
            },
            loadingFilters() {
                return this.campaignsLoading || this.spaceGroupsLoading || this.spaceProductsLoading || this.categoriesLoading;
            },
            loading() {
                return this.requestData1.getLoading() || this.requestData2.getLoading() || this.loadingFilters;
            },
            data() {
                return Object.values(this.requestData1.getData() || {});
            },
            data2() {
                return this.requestData2.getData() || [];
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
            },
            apiParams: {
                immediate: false,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
        },
        methods: {
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.requestData1.abortAll();
                this.requestData1.resetAll();
                this.requestData2.abortAll();
                this.requestData2.resetAll();
            },
            fetchData() {
                this.requestData1.get(this.apiParams);
                this.requestData2.get(this.apiParams);
            },
            resetFilters() {
                this.showProductGroupsResults = false;
                this.campaigns = [];
                this.groups = [];
                this.products = [];
                this.categories = [];
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            fetchProducerProducts({ search }) {
                return this.producerProduct.get({ urlParams: { spaceId: this.spaceId }, requestParams: { search } });
            },
        },
        created() {
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>
